import React from "react";
import { FaUsers } from "react-icons/fa";
import { useAuth } from "../Context/AuthContext";
import { privateRoutes } from "../routes/routes";
import {
  GLOBAL_MY_ACCOUNT_ITEMS,
  GLOBAL_ADMINISTRATION_ITEMS,
  GLOBAL_GOVERNANCE_ITEMS,
  GLOBAL_GROWTH_ITEMS,
} from "../config/globalSideNavConfig";

const SERVICE_LABELS = { myFpo: "My Org", mybusiness: "My Business" };

// Flattens one service's annotated getModules() output (routes.js) into a lookup keyed by each
// module's own `path` — the same generic annotate()/computeAccessible logic Service-mode already
// relies on, so `.locked` here means exactly what it means there: requiredPermission/allowedRoles/
// systemAdmin already evaluated, including the "parent unlocked if any real child is" rule for
// modules with `children`. Only one level of nesting exists today (My Account, Compliances), but
// this walks recursively rather than assuming that.
function flattenModulesByPath(modules) {
  const byPath = {};
  const visit = (module) => {
    if (module.path) byPath[module.path] = module;
    module.children?.forEach(visit);
  };
  modules.forEach(visit);
  return byPath;
}

// Resolves one Global-mode projection row (config/globalSideNavConfig.js) against both services'
// real getModules() output. SIDENAV_REDESIGN_PLAN.md Part 2's "never silently choose" rule: null
// (hide the row) when neither service is usable, a direct path when exactly one is, or a
// `mode: "choose"` result (both usable) for ServiceSelectPrompt.jsx to render.
function resolveProjectionItem(row, { modulesByService, hasServiceAccess }) {
  const usable = ["myFpo", "mybusiness"]
    .map((serviceKey) => {
      if (!hasServiceAccess(serviceKey)) return null;
      const module = modulesByService[serviceKey][row.path];
      if (!module || module.locked) return null;
      return { serviceKey, path: `/${serviceKey}/${row.path}${row.query || ""}` };
    })
    .filter(Boolean);

  if (usable.length === 0) return null;
  if (usable.length === 1) return { mode: "direct", label: row.label, path: usable[0].path, icon: row.icon };
  return {
    mode: "choose",
    label: row.label,
    icon: row.icon,
    options: usable.map((u) => ({
      serviceKey: u.serviceKey,
      path: u.path,
      serviceLabel: SERVICE_LABELS[u.serviceKey],
    })),
  };
}

// The Global-mode SideNav's resolver — mirrors Part 1's getModules()/annotate pattern, and as of
// this refactor actually reuses it: myAccount/governance/growth are a *projection* of the same
// getModules() output Service-mode renders from (see config/globalSideNavConfig.js), not a second
// hand-maintained definition. `internalUser` picks External vs. Internal structure; every leaf is
// then permission-filtered on top of that.
export function useGlobalSideNav() {
  const { fullUserData, normalizedRoles, hasEntityAccess, hasServiceAccess } = useAuth();
  const isInternal = fullUserData?.userTypeEntity?.internalUser === true;

  const getModulesFor = (serviceKey) =>
    privateRoutes
      .find((route) => route.serviceKey === serviceKey)
      ?.getModules(normalizedRoles, hasEntityAccess, fullUserData) || [];

  const modulesByService = {
    myFpo: flattenModulesByPath(getModulesFor("myFpo")),
    mybusiness: flattenModulesByPath(getModulesFor("mybusiness")),
  };
  const ctx = { modulesByService, hasServiceAccess };

  const myAccount = !isInternal
    ? GLOBAL_MY_ACCOUNT_ITEMS.map((row) => resolveProjectionItem(row, ctx)).filter(Boolean)
    : [];
  const governance = GLOBAL_GOVERNANCE_ITEMS.map((row) => resolveProjectionItem(row, ctx)).filter(Boolean);
  const growth = GLOBAL_GROWTH_ITEMS.map((row) => resolveProjectionItem(row, ctx)).filter(Boolean);
  const administration = isInternal
    ? GLOBAL_ADMINISTRATION_ITEMS.filter((item) => normalizedRoles?.systemAdmin || hasEntityAccess(item.entityKey, "R"))
    : [];

  const sections = [
    !isInternal && myAccount.length > 0 && { key: "myAccount", label: "My Account", items: myAccount },
    // Icon matches Service-mode's own My Directory icon (routes.js) — same concept, same glyph.
    { key: "directory", label: "Directory", items: [{ mode: "comingSoon", label: "My Directory", icon: <FaUsers /> }] },
    governance.length > 0 && { key: "governance", label: "Governance & Compliance", items: governance },
    isInternal && administration.length > 0 && {
      key: "administration",
      label: "Administration",
      items: administration.map((a) => ({ mode: "direct", label: a.label, path: a.path, icon: a.icon })),
    },
    growth.length > 0 && { key: "growth", label: "Growth & Reports", items: growth },
  ].filter(Boolean);

  return { isInternal, sections };
}
